import React, { useEffect, useState } from "react";
import { FiX, FiUser, FiLock } from "react-icons/fi";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import adminAxios from "../../utils/adminAxios";
import { updateStoredAdmin } from "../../utils/adminAuth";

const MyProfile = ({ open, onClose, admin, onUpdated }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    adminName: "",
    currentPassword: "",
    newPassword: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        adminName: admin?.adminName || "",
        currentPassword: "",
        newPassword: "",
      });
    }
  }, [open, admin]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (form.newPassword && !form.currentPassword) {
      toast.error(t("dashboard.myProfile.messages.currentPasswordRequired"));
      return;
    }

    setSaving(true);
    try {
      const response = await adminAxios.patch("admins/profile", {
        adminName: form.adminName,
        ...(form.newPassword
          ? {
              currentPassword: form.currentPassword,
              newPassword: form.newPassword,
            }
          : {}),
      });
      const updatedAdmin = response.data.data;
      updateStoredAdmin(updatedAdmin);
      toast.success(t("dashboard.myProfile.messages.updateSuccess"));
      if (onUpdated) onUpdated(updatedAdmin);
      onClose();
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          t("dashboard.myProfile.messages.genericError")
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              {t("dashboard.myProfile.title")}
            </h3>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t("dashboard.myProfile.fields.adminName")}
              </label>
              <div className="relative">
                <FiUser className="absolute left-3 rtl:left-auto rtl:right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  required
                  value={form.adminName}
                  onChange={(e) =>
                    setForm({ ...form, adminName: e.target.value })
                  }
                  className="w-full pl-10 pr-4 rtl:pl-4 rtl:pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                {t("dashboard.myProfile.passwordHint")}
              </p>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t("dashboard.myProfile.fields.currentPassword")}
                </label>
                <div className="relative">
                  <FiLock className="absolute left-3 rtl:left-auto rtl:right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="password"
                    value={form.currentPassword}
                    onChange={(e) =>
                      setForm({ ...form, currentPassword: e.target.value })
                    }
                    className="w-full pl-10 pr-4 rtl:pl-4 rtl:pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t("dashboard.myProfile.fields.newPassword")}
                </label>
                <div className="relative">
                  <FiLock className="absolute left-3 rtl:left-auto rtl:right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="password"
                    minLength={6}
                    value={form.newPassword}
                    onChange={(e) =>
                      setForm({ ...form, newPassword: e.target.value })
                    }
                    className="w-full pl-10 pr-4 rtl:pl-4 rtl:pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 rtl:space-x-reverse pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                {t("dashboard.myProfile.buttons.cancel")}
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg shadow-md hover:shadow-lg transition-all disabled:opacity-50"
              >
                {saving
                  ? t("dashboard.myProfile.buttons.saving")
                  : t("dashboard.myProfile.buttons.save")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MyProfile;
